// Iter76 — Partner / Franchisee Product Management
// ================================================
// Single-source page for partners to create, edit, submit, and manage their
// product catalog. Uses the unified `partner_product_drafts` pipeline:
//   POST /api/partner/products              → create draft
//   PUT  /api/partner/products/{id}          → edit draft
//   POST /api/partner/products/{id}/submit   → submit for admin review
//   POST /api/partner/products/{id}/featured → toggle featured (max 2)
//   DELETE /api/partner/products/{id}        → delete draft
//
// • Multi-image DEVICE upload (FileReader → base64 → server hybrid-stores
//   to Cloudinary or inline DB so the image NEVER vanishes).
// • Category-driven dynamic fields (Glass Gear, Clothing, Accessories,
//   Vape Hardware, Food/Bev, Topicals, Tinctures, Edibles, CBD, THCA, Other).
// • THCA / restricted products show extra required fields (age, potency,
//   label image, COA) — server hard-blocks submit if any are missing.
// • Featured toggle (max 2 approved products per partner).
//
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Image as ImageIcon,
  Loader2,
  Plus,
  Send,
  Star,
  Trash2,
  X,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import SafeImage from "@/components/SafeImage";
import { ProductImg } from "@/lib/productImage";

// ---------- helpers ----------
const STATUS_BADGES = {
  draft:           { label: "Draft",           cls: "bg-slate-500/20 text-slate-200 border-slate-400/30" },
  pending_review:  { label: "Pending Review",  cls: "bg-amber-500/20 text-amber-200 border-amber-400/30" },
  compliance_review: { label: "Compliance Review", cls: "bg-amber-500/20 text-amber-200 border-amber-400/30" },
  action_required: { label: "Action Required", cls: "bg-rose-500/20 text-rose-200 border-rose-400/30" },
  approved:        { label: "Approved · Live", cls: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30" },
  rejected:        { label: "Rejected",        cls: "bg-rose-500/30 text-rose-100 border-rose-400/40" },
};
function StatusBadge({ status }) {
  const meta = STATUS_BADGES[status] || { label: status, cls: "bg-white/10 text-white border-white/20" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-bebas tracking-wider ${meta.cls}`}
          data-testid={`partner-product-status-${status}`}>
      {meta.label}
    </span>
  );
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// ---------- category schema (mirrors backend CATEGORY_KEYS) ----------
const CATEGORY_DEFS = {
  glass_gear: {
    label: "Glass Gear",
    fields: [
      { key: "glass_type", label: "Type (rig/pipe/bong/accessory)", placeholder: "rig" },
      { key: "material", label: "Material", placeholder: "borosilicate glass" },
      { key: "color", label: "Color", placeholder: "clear" },
    ],
  },
  clothing: {
    label: "Clothing / Lifted Gear",
    fields: [
      { key: "garment_type", label: "Type (tee/hoodie/cap/etc.)", placeholder: "hoodie" },
      { key: "size_options", label: "Size options (comma-separated)", placeholder: "S, M, L, XL" },
      { key: "color_options", label: "Color options (comma-separated)", placeholder: "black, olive" },
    ],
  },
  accessories: {
    label: "Accessories / Lifted Accessories",
    fields: [
      { key: "accessory_type", label: "Accessory type", placeholder: "grinder" },
      { key: "color", label: "Color", placeholder: "black" },
    ],
  },
  vape_hardware: {
    label: "Vape Hardware",
    fields: [
      { key: "hardware_type", label: "Hardware type", placeholder: "battery / cartridge / pod" },
      { key: "brand_model", label: "Brand / Model", placeholder: "Brand X · Model Y" },
      { key: "compatibility", label: "Compatibility", placeholder: "510-thread" },
    ],
  },
  food_beverage: {
    label: "Food & Beverage",
    fields: [
      { key: "type", label: "Product type", placeholder: "beverage" },
      { key: "ingredients", label: "Ingredients / description", placeholder: "..." },
      { key: "serving_info", label: "Serving / package size", placeholder: "12 oz can" },
    ],
  },
  topicals: {
    label: "Topicals",
    fields: [
      { key: "type", label: "Product type", placeholder: "salve / lotion" },
      { key: "ingredients", label: "Ingredients", placeholder: "..." },
      { key: "package_info", label: "Package info", placeholder: "2 oz jar" },
    ],
  },
  tinctures: {
    label: "Tinctures",
    fields: [
      { key: "type", label: "Type", placeholder: "full-spectrum" },
      { key: "ingredients", label: "Ingredients", placeholder: "..." },
      { key: "serving_info", label: "Serving / dropper size", placeholder: "1 mL = 25mg" },
    ],
  },
  edibles: {
    label: "Edibles",
    fields: [
      { key: "type", label: "Type", placeholder: "gummy / chocolate" },
      { key: "ingredients", label: "Ingredients", placeholder: "..." },
      { key: "serving_info", label: "Serving info", placeholder: "10mg / piece, 10 pieces" },
    ],
  },
  cbd: {
    label: "CBD",
    fields: [
      { key: "type", label: "Type", placeholder: "isolate / full-spectrum" },
      { key: "ingredients", label: "Ingredients", placeholder: "..." },
      { key: "package_info", label: "Package info", placeholder: "1000mg / 30ml" },
    ],
  },
  thca: {
    label: "THCA / THC-Restricted",
    restricted: true,
    fields: [
      { key: "type", label: "Type", placeholder: "flower / vape / edible" },
      { key: "potency_note", label: "Potency note", placeholder: "Δ9 THC ≤ 0.3% by dry weight" },
    ],
  },
  other: {
    label: "Other",
    fields: [
      { key: "type", label: "Type", placeholder: "describe..." },
    ],
  },
};

const RESTRICTED_KEYS = new Set(
  Object.entries(CATEGORY_DEFS).filter(([, v]) => v.restricted).map(([k]) => k),
);

// ---------- empty draft ----------
const EMPTY_DRAFT = {
  id: null,
  title: "",
  description: "",
  category: "",
  category_fields: {},
  cost_cents: 0,
  price_cents: 0,
  inventory_count: 0,
  images: [],
  same_day_eligible: false,
  age_restricted: false,
  thc_pct: "",
  cbd_pct: "",
  label_image_url: "",
  coa_url: "",
  state_restrictions: [],
};

function priceToInput(c) { return c ? (Number(c) / 100).toFixed(2) : ""; }
function inputToCents(s) {
  const n = parseFloat(s);
  if (Number.isNaN(n) || n < 0) return 0;
  return Math.round(n * 100);
}

// ---------- main page ----------
export default function PartnerProducts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDraft, setEditingDraft] = useState(null);   // null = not editing
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const { data } = await api.get("/partner/products");
      setItems(data.items || []);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, []);

  async function openNew() {
    setEditingDraft({ ...EMPTY_DRAFT });
    setError(null); setInfo(null);
  }
  async function openEdit(d) {
    setEditingDraft({
      ...EMPTY_DRAFT,
      ...d,
      cost_cents: d.cost_cents || 0,
      price_cents: d.price_cents || 0,
      inventory_count: d.inventory_count || 0,
      images: d.images || [],
      category_fields: d.category_fields || {},
      same_day_eligible: !!d.same_day_eligible,
    });
    setError(null); setInfo(null);
  }

  async function save({ submit }) {
    const d = editingDraft;
    if (!d.title.trim()) { setError("Add a product title."); return; }
    if (!d.category) { setError("Choose a category."); return; }
    if (!d.price_cents || d.price_cents < 1) { setError("Add a retail price."); return; }
    setError(null);

    const payload = {
      title: d.title,
      description: d.description,
      category: d.category,
      category_fields: d.category_fields || {},
      cost_cents: d.cost_cents || 0,
      price_cents: d.price_cents,
      inventory_count: d.inventory_count || 0,
      images: d.images || [],
      same_day_eligible: !!d.same_day_eligible,
      age_restricted: !!d.age_restricted,
      thc_pct: d.thc_pct || null,
      cbd_pct: d.cbd_pct || null,
      label_image_url: d.label_image_url || null,
      coa_url: d.coa_url || null,
      state_restrictions: d.state_restrictions || [],
    };

    try {
      let saved;
      if (d.id) {
        const { data } = await api.put(`/partner/products/${d.id}`, payload);
        saved = data.product;
      } else {
        const { data } = await api.post("/partner/products", payload);
        saved = data.product;
      }
      if (submit && saved?.id) {
        await api.post(`/partner/products/${saved.id}/submit`);
        setInfo("Submitted for admin review.");
      } else {
        setInfo("Draft saved.");
      }
      setEditingDraft(null);
      await reload();
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  async function submitDraft(id) {
    try {
      await api.post(`/partner/products/${id}/submit`);
      setInfo("Submitted for admin review.");
      await reload();
    } catch (e) { setError(apiErrorMessage(e)); }
  }
  async function deleteDraft(id) {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/partner/products/${id}`);
      setInfo("Product deleted.");
      await reload();
    } catch (e) { setError(apiErrorMessage(e)); }
  }
  async function toggleFeatured(p) {
    try {
      await api.post(`/partner/products/${p.id}/featured`, { featured: !p.featured });
      await reload();
    } catch (e) { setError(apiErrorMessage(e)); }
  }

  const featuredCount = useMemo(
    () => items.filter((i) => i.featured && i.status === "approved").length,
    [items],
  );

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-7xl" data-testid="partner-product-submit-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-[0.2em] text-lifted-green">Partner · Product Catalog</div>
          <h1 className="font-anton uppercase text-4xl text-white">Your Products</h1>
          <p className="text-lifted-dim text-sm max-w-2xl">
            Upload product images from your device. Submit for admin review.
            Once approved, your product is live in the matching Lifted category
            AND your individual storefront.
          </p>
          <div className="text-xs text-white/60">
            Featured: <span data-testid="partner-featured-count">{featuredCount}</span> / 2 ·
            Approved live products are purchasable site-wide.
          </div>
        </div>
        <button
          onClick={openNew}
          data-testid="partner-product-new-btn"
          className="btn-lifted inline-flex items-center gap-2"
        >
          <Plus className="h-4 w-4" /> New Product
        </button>
      </header>

      {error && (
        <div data-testid="partner-product-error"
             className="border border-rose-500/40 bg-rose-500/10 rounded-md p-3 text-sm text-rose-200 flex items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}
      {info && (
        <div data-testid="partner-product-info"
             className="border border-emerald-500/40 bg-emerald-500/10 rounded-md p-3 text-sm text-emerald-200 flex items-center gap-2">
          <CheckCircle2 size={14} /> {info}
        </div>
      )}

      {/* ---------- LIST ---------- */}
      {loading ? (
        <div className="flex items-center gap-2 text-lifted-dim"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center text-lifted-dim py-12 border border-dashed border-white/15 rounded-xl">
          <ImageIcon className="mx-auto h-8 w-8 mb-3 opacity-60" />
          No products yet. Click <strong>New Product</strong> to start.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <article
              key={p.id}
              data-testid={`partner-product-card-${p.id}`}
              className="glass-panel p-4 space-y-3"
            >
              <div className="aspect-square w-full bg-black/30 rounded-md overflow-hidden">
                <ProductImg product={p} alt={p.title} className="w-full h-full object-cover" data-testid={`partner-product-image-card-${p.id}`} />
              </div>
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-1">
                  <div className="text-sm font-semibold text-white" data-testid={`partner-product-title-${p.id}`}>{p.title}</div>
                  <div className="text-xs text-lifted-dim">{CATEGORY_DEFS[p.category]?.label || p.category}</div>
                </div>
                <StatusBadge status={p.status} />
              </div>
              <div className="flex items-center justify-between text-xs text-white/70">
                <span>${priceToInput(p.price_cents)}</span>
                <span>Stock: {p.inventory_count ?? 0}</span>
              </div>
              {p.square_sync_status === "failed" && (
                <div className="text-xs text-rose-300 border border-rose-400/30 bg-rose-500/10 rounded p-2"
                     data-testid={`partner-product-square-error-${p.id}`}>
                  Square sync failed: {p.square_sync_error || "unknown"}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {p.status === "draft" || p.status === "rejected" || p.status === "action_required" ? (
                  <button
                    onClick={() => submitDraft(p.id)}
                    data-testid={`partner-product-submit-${p.id}`}
                    className="btn-lifted-outline inline-flex items-center gap-1 text-xs"
                  >
                    <Send size={12} /> Submit
                  </button>
                ) : null}
                <button
                  onClick={() => openEdit(p)}
                  data-testid={`partner-product-edit-${p.id}`}
                  className="btn-lifted-outline inline-flex items-center gap-1 text-xs"
                >
                  Edit
                </button>
                {p.status === "approved" && (
                  <button
                    onClick={() => toggleFeatured(p)}
                    data-testid={`partner-product-featured-${p.id}`}
                    className={`btn-lifted-outline inline-flex items-center gap-1 text-xs ${p.featured ? "bg-lifted-green/20 border-lifted-green/40" : ""}`}
                  >
                    <Star size={12} className={p.featured ? "text-lifted-green" : ""} />
                    {p.featured ? "Featured" : "Make featured"}
                  </button>
                )}
                {p.status !== "approved" && (
                  <button
                    onClick={() => deleteDraft(p.id)}
                    data-testid={`partner-product-delete-${p.id}`}
                    className="btn-lifted-outline inline-flex items-center gap-1 text-xs text-rose-300 hover:text-rose-200"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                )}
              </div>
              {p.rejected_reason && (
                <div className="text-xs text-rose-200 bg-rose-500/10 rounded p-2"
                     data-testid={`partner-product-reject-reason-${p.id}`}>
                  Reason: {p.rejected_reason}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {/* ---------- EDIT MODAL ---------- */}
      {editingDraft && (
        <DraftEditor
          draft={editingDraft}
          onChange={setEditingDraft}
          onCancel={() => setEditingDraft(null)}
          onSave={save}
        />
      )}

      <div className="text-xs text-lifted-dim">
        Need to send updates to compliance? Visit your{" "}
        <Link to="/partner/compliance" className="underline text-lifted-green">compliance dashboard</Link>.
      </div>
    </div>
  );
}

// ---------- DraftEditor (modal/inline form) ----------
function DraftEditor({ draft, onChange, onCancel, onSave }) {
  const cat = CATEGORY_DEFS[draft.category];
  const isRestricted = RESTRICTED_KEYS.has(draft.category);
  const [uploading, setUploading] = useState(false);

  function patch(p) { onChange((d) => ({ ...d, ...p })); }
  function patchCF(k, v) {
    onChange((d) => ({ ...d, category_fields: { ...(d.category_fields || {}), [k]: v } }));
  }

  async function onPickImages(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const out = await Promise.all(files.map(fileToDataUrl));
      onChange((d) => ({ ...d, images: [...(d.images || []), ...out] }));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }
  async function onPickLabel(e) {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setUploading(true);
    try {
      const url = await fileToDataUrl(f);
      patch({ label_image_url: url });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }
  function removeImage(i) {
    onChange((d) => ({ ...d, images: (d.images || []).filter((_, idx) => idx !== i) }));
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 grid place-items-center p-4" data-testid="partner-product-editor">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-lifted-surface border border-white/10 rounded-2xl p-6 space-y-4">
        <div className="flex items-start justify-between">
          <h2 className="font-anton uppercase text-2xl text-white">
            {draft.id ? "Edit product" : "New product"}
          </h2>
          <button onClick={onCancel} className="p-2 hover:bg-white/10 rounded" data-testid="partner-product-editor-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Category — first decision */}
        <Field label="Category" required>
          <select
            value={draft.category}
            onChange={(e) => patch({ category: e.target.value, category_fields: {} })}
            className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/15 text-white"
            data-testid="partner-product-category"
          >
            <option value="">Choose…</option>
            {Object.entries(CATEGORY_DEFS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}{v.restricted ? "  ·  RESTRICTED" : ""}</option>
            ))}
          </select>
        </Field>

        <Field label="Title" required>
          <input
            type="text"
            value={draft.title}
            onChange={(e) => patch({ title: e.target.value })}
            placeholder="e.g. Hand-blown borosilicate rig"
            className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/15 text-white"
            data-testid="partner-product-title"
          />
        </Field>

        <Field label="Description">
          <textarea
            rows={3}
            value={draft.description}
            onChange={(e) => patch({ description: e.target.value })}
            className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/15 text-white"
            data-testid="partner-product-description"
          />
        </Field>

        {/* Category-specific fields */}
        {cat && cat.fields?.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {cat.fields.map((f) => (
              <Field key={f.key} label={f.label}>
                <input
                  type="text"
                  value={draft.category_fields?.[f.key] || ""}
                  onChange={(e) => patchCF(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/15 text-white"
                  data-testid={`partner-product-cf-${f.key}`}
                />
              </Field>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Cost ($)" helper="What it costs you">
            <input
              type="number" min="0" step="0.01"
              value={priceToInput(draft.cost_cents)}
              onChange={(e) => patch({ cost_cents: inputToCents(e.target.value) })}
              className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/15 text-white"
              data-testid="partner-product-cost"
            />
          </Field>
          <Field label="Retail Price ($)" required>
            <input
              type="number" min="0" step="0.01"
              value={priceToInput(draft.price_cents)}
              onChange={(e) => patch({ price_cents: inputToCents(e.target.value) })}
              className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/15 text-white"
              data-testid="partner-product-price"
            />
          </Field>
          <Field label="Inventory">
            <input
              type="number" min="0"
              value={draft.inventory_count || 0}
              onChange={(e) => patch({ inventory_count: parseInt(e.target.value || "0", 10) })}
              className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/15 text-white"
              data-testid="partner-product-inventory"
            />
          </Field>
        </div>

        {/* Iter80 — Same-day delivery eligibility toggle */}
        <div className="border border-white/10 rounded-md p-3">
          <label className="inline-flex items-center gap-2 text-sm text-white cursor-pointer">
            <input
              type="checkbox"
              checked={!!draft.same_day_eligible}
              onChange={(e) => patch({ same_day_eligible: e.target.checked })}
              data-testid="partner-product-same-day"
            />
            Available for <strong>same-day delivery</strong> when the customer is in our service area
          </label>
        </div>

        {/* Images from device */}
        <div>
          <label className="block text-xs uppercase tracking-wider text-lifted-dim mb-2">
            Product images <span className="text-rose-300">*</span>
          </label>
          <div className="flex flex-wrap gap-3">
            {(draft.images || []).map((img, i) => (
              <div key={i} className="relative w-24 h-24 rounded-md overflow-hidden border border-white/15" data-testid={`partner-product-image-${i}`}>
                <SafeImage src={img} alt={`image ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => removeImage(i)}
                  className="absolute top-1 right-1 p-1 bg-black/60 rounded text-white hover:bg-rose-600"
                  data-testid={`partner-product-image-remove-${i}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            <label className="w-24 h-24 border border-dashed border-white/30 rounded-md grid place-items-center cursor-pointer hover:border-lifted-green text-xs text-lifted-dim">
              <input type="file" accept="image/*" multiple className="hidden" onChange={onPickImages}
                     data-testid="partner-product-image-picker" />
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus size={14} /><br />Add image</>}
            </label>
          </div>
          <p className="text-xs text-lifted-dim mt-1">Upload from device. Images persist after refresh / re-login.</p>
        </div>

        {/* Restricted fields */}
        {isRestricted && (
          <div className="border border-amber-500/40 bg-amber-500/5 rounded-md p-4 space-y-3" data-testid="partner-product-restricted-section">
            <div className="flex items-center gap-2 text-amber-200 text-sm uppercase font-bebas tracking-wider">
              <AlertTriangle size={14} /> Restricted Product · All fields required
            </div>
            <Field label="Age restricted (21+)">
              <label className="inline-flex items-center gap-2 text-sm text-white">
                <input
                  type="checkbox"
                  checked={!!draft.age_restricted}
                  onChange={(e) => patch({ age_restricted: e.target.checked })}
                  data-testid="partner-product-age-restricted"
                />
                Yes, this product requires 21+ to purchase
              </label>
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="THC % / potency" required>
                <input
                  type="text"
                  value={draft.thc_pct || ""}
                  onChange={(e) => patch({ thc_pct: e.target.value })}
                  placeholder="≤ 0.3% Δ9-THC"
                  className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/15 text-white"
                  data-testid="partner-product-thc-pct"
                />
              </Field>
              <Field label="CBD % (if applicable)">
                <input
                  type="text"
                  value={draft.cbd_pct || ""}
                  onChange={(e) => patch({ cbd_pct: e.target.value })}
                  placeholder="10%"
                  className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/15 text-white"
                  data-testid="partner-product-cbd-pct"
                />
              </Field>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-lifted-dim mb-2">
                Product label image <span className="text-rose-300">*</span>
              </label>
              {draft.label_image_url ? (
                <div className="flex items-center gap-3">
                  <SafeImage src={draft.label_image_url} className="w-24 h-24 rounded object-cover border border-white/15" alt="label" />
                  <button onClick={() => patch({ label_image_url: "" })} className="text-xs text-rose-300 hover:text-rose-200"
                          data-testid="partner-product-label-image-remove">Remove</button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-2 text-xs cursor-pointer text-lifted-green border border-lifted-green/40 px-3 py-2 rounded hover:bg-lifted-green/10">
                  <input type="file" accept="image/*" className="hidden" onChange={onPickLabel}
                         data-testid="partner-product-label-image-picker" />
                  <ImageIcon size={14} /> Upload label image
                </label>
              )}
            </div>
            <Field label="COA / Lab report URL (or paste data URI)" required>
              <input
                type="text"
                value={draft.coa_url || ""}
                onChange={(e) => patch({ coa_url: e.target.value })}
                placeholder="https://…coa.pdf"
                className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/15 text-white"
                data-testid="partner-product-coa-url"
              />
            </Field>
            <Field label="State restrictions (comma-separated, optional)">
              <input
                type="text"
                value={(draft.state_restrictions || []).join(", ")}
                onChange={(e) => patch({ state_restrictions: e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) })}
                placeholder="IA, ID, KS"
                className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/15 text-white"
                data-testid="partner-product-state-restrictions"
              />
            </Field>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-white/10">
          <button onClick={onCancel} className="btn-lifted-outline" data-testid="partner-product-editor-cancel">Cancel</button>
          <button
            onClick={() => onSave({ submit: false })}
            className="btn-lifted-outline inline-flex items-center gap-1"
            data-testid="partner-product-editor-save-draft"
          >
            <Clock size={14} /> Save draft
          </button>
          <button
            onClick={() => onSave({ submit: true })}
            className="btn-lifted inline-flex items-center gap-1"
            data-testid="partner-product-editor-submit"
          >
            <Send size={14} /> Submit for review
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, helper, required, children }) {
  return (
    <label className="block space-y-1">
      <div className="text-xs uppercase tracking-wider text-lifted-dim">
        {label} {required && <span className="text-rose-300">*</span>}
      </div>
      {children}
      {helper && <div className="text-[10px] text-lifted-dim/70">{helper}</div>}
    </label>
  );
}
